
    import {inject, ref, watch, computed} from 'vue';
    import {RouteLocationNormalized, useRoute} from 'vue-router';
    import {getPageLanguageCode} from '@malt/dom';
    import Autocomplete from '@malt/joy-entrypoint/src/vue/Autocomplete.vue';
    import TooltipInfo from '@/brief-hub/common/ui/components/TooltipInfo.vue';
    import {DI} from '@/brief-hub/config/DependencyInjection';
    import {FreelancerSkillsService} from '@/common/api/FreelancerSkillsService';
    import {LanguagesService} from '@/brief-hub/brief/LanguagesService';
    import {MutationsType} from '@/brief-hub/store/modules/brief/mutations';
    import {Skill} from '@/common/types/Skill';
    import type {BriefFreelancerProfile, BriefFreelancerSkills} from '@/brief-hub/store/modules/brief/types';
    import {LanguageResource} from '@/common/api/api-raw-types';
    import store from '@/brief-hub/store';
    import {useGlobalProperties, useTranslation} from '@malt/vue3-use';
    import {useSkills} from '@/common/composable/useSkills';
    import ProjectProposalSkillsField from '@/common/components/ProjectProposalSkillsField.vue';
    import {BriefFreelancerJobTitle} from '@/brief-hub/store/modules/brief/types';
    import {BriefTrackingService} from '@/brief-hub/brief/BriefTrackingService';
    import BriefHubStepLayout from '@/brief-hub/common/ui/layout/BriefHubStepLayout.vue';

    export default {
        name: 'BriefFreelancerSkills',
        components: {
            ProjectProposalSkillsField,
            BriefHubStepLayout,
            TooltipInfo,
            Autocomplete,
        },
        beforeRouteEnter(to: RouteLocationNormalized, from: RouteLocationNormalized, next: (vm: any) => void) {
            next((vm: any) => {
                vm.onFormChange;
            });
        },

        setup() {
            const {$features} = useGlobalProperties();
            const route = useRoute();
            const {msg} = useTranslation();
            const freelancerSkillsService = inject<FreelancerSkillsService>(DI.freelancerSkillsService);
            const languagesService = inject<LanguagesService>(DI.languagesService);
            const briefTrackingService = inject<BriefTrackingService>(DI.briefTrackingService);

            const briefFreelancerProfile: BriefFreelancerProfile = store.getters.briefFreelancerProfile;
            const briefFreelancerJobTitle: BriefFreelancerJobTitle = store.getters.briefFreelancerJobTitle;
            const briefFreelancerSkills: BriefFreelancerSkills = store.getters.briefFreelancerSkills;
            const {skills, maxSkills, minSkills, suggestedSkills, checkSuggestedSkills} = useSkills(
                briefFreelancerSkills.selectedSkills.map((s) => new Skill(s)),
                freelancerSkillsService!,
            );
            const languages = ref<LanguageResource[]>([]);
            const selectedCategory = briefFreelancerProfile.selectedCategory;
            const jobTitle = briefFreelancerJobTitle.jobTitle;
            const pageLanguageCode = getPageLanguageCode();

            let initialized = false;

            watch(
                () => languages,
                () => {
                    onFormChange();
                },
                {deep: true},
            );

            async function init(): Promise<void> {
                await languagesService!.getAllLanguages();
                if (briefFreelancerSkills) {
                    skills.value = [...briefFreelancerSkills.selectedSkills.map((s) => new Skill(s))];
                    languages.value = briefFreelancerSkills.selectedLanguages;
                }
                setPageLanguageSkillForNewBrief();
                briefTrackingService?.triggerBriefFunnelStepEnteredAmplitudeEvent('skills', 2, route);
            }

            function initializeComponent() {
                initialized = true;
            }

            function languageValueChanged(autoCompletedValue: LanguageResource): void {
                if (!autoCompletedValue) {
                    return;
                }

                const alreadyPresent = languages.value.find((language) => language.code === autoCompletedValue.code);

                if (!alreadyPresent) {
                    languages.value.push(autoCompletedValue);
                }
            }

            function setPageLanguageSkillForNewBrief() {
                const isFirstTimeSkillsScreenDisplayed = store.state.brief.briefForm.skills === null && skills.value.length === 0;
                if (isFirstTimeSkillsScreenDisplayed && pageLanguageCode) {
                    // @ts-ignore
                    const language = new Intl.DisplayNames([pageLanguageCode], {type: 'language'}).of(pageLanguageCode);
                    if (language) {
                        languageValueChanged({code: pageLanguageCode, label: language});
                    }
                }
            }

            function removeLanguage(index: number): void {
                languages.value.splice(index, 1);
            }

            function onFormChange() {
                checkSuggestedSkills();
                if (skills.value.length >= minSkills && skills.value.length <= maxSkills) {
                    store.commit(MutationsType.UPDATE_PAYLOAD, {
                        type: 'FreelancerSkillsBriefRequest',
                        payload: {
                            freelancerSkills: skills.value.map((s) => s.name),
                            languageCodes: languages.value.map((l) => l.code),
                        },
                    });
                } else {
                    store.commit(MutationsType.UPDATE_PAYLOAD, null);
                }
                if (initialized) {
                    store.commit(MutationsType.UPDATE_BRIEF_FORM_SKILLS, {
                        selectedSkills: skills.value.map((s) => s.name),
                        selectedLanguages: languages.value,
                    });
                }
            }

            function updateSkills(newSkills: Skill[]) {
                skills.value = newSkills;
                onFormChange();
            }

            init();

            return {
                initializeComponent,
                freelancerSkillsService,
                suggestedSkills,
                minSkills,
                skills,
                maxSkills,
                languagesService,
                languageValueChanged,
                languages,
                removeLanguage,
                updateSkills,
                jobTitle,
                selectedCategory,
            };
        },
    };
